import { Injectable, NgZone, inject } from '@angular/core';
import { Subject, fromEvent, merge, timer, Observable, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService {
  private ngZone = inject(NgZone);
  private authService = inject(AuthService);

  // Configuration - use 2 minutes for testing, 20 minutes for production
  private readonly TESTING_MODE = false; // Set to false for production
  private IDLE_TIMEOUT = this.TESTING_MODE ? 2 * 60 * 1000 : 20 * 60 * 1000; // 2 min or 20 min in milliseconds
  private WARNING_TIME = this.TESTING_MODE ? 0.2 * 60 * 1000 : 2 * 60 * 1000; // 12 seconds (0.2 min) or 2 min before timeout
  
  // Subjects for events
  private idleWarning$ = new Subject<number>();
  private idleTimeout$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  
  // Timer state
  private warningTimer: any;
  private timeoutTimer: any;
  private isWarningActive = false;
  private warningStartTime: number = 0;
  private countdownInterval: any;
  private authSubscription: Subscription | null = null;
  private isWatching = false;

  constructor() {
    this.authSubscription = this.authService.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        const user = this.authService.getCurrentUser();
        if (user && user.autoLogOutOnIdleTime) {
          // autoLogOutOnIdleTime is expected in minutes
          const idleTimeMs = Number(user.autoLogOutOnIdleTime) * 60 * 1000;
          if (!isNaN(idleTimeMs) && idleTimeMs > 0) {
            this.IDLE_TIMEOUT = idleTimeMs;
            // Set warning time to 2 minutes or 20% of idle time, whichever is smaller
            this.WARNING_TIME = Math.min(2 * 60 * 1000, this.IDLE_TIMEOUT * 0.2);
          }
        }
        
        if (this.TESTING_MODE) {
          this.IDLE_TIMEOUT = 2 * 60 * 1000;
          this.WARNING_TIME = 0.2 * 60 * 1000;
        }

        this.startWatching();
      } else {
        this.stopWatching();
      }
    });
  }

  /**
   * Get the warning observable
   */
  getWarningObservable(): Observable<number> {
    return this.idleWarning$.asObservable();
  }

  /**
   * Get the timeout observable
   */
  getTimeoutObservable(): Observable<void> {
    return this.idleTimeout$.asObservable();
  }

  /**
   * Start watching for user activity
   */
  private startWatching(): void {
    if (this.isWatching) return;
    this.isWatching = true;
    
    // Recreate destroy subject if it was completed
    if (this.destroy$.isStopped) {
      this.destroy$ = new Subject<void>();
    }

    this.ngZone.runOutsideAngular(() => {
      // Listen to user activity events
      const events$ = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'mousedown'),
        fromEvent(document, 'keypress'),
        fromEvent(document, 'touchstart'),
        fromEvent(document, 'scroll'),
        fromEvent(document, 'wheel')
      );

      // Debounce to avoid excessive processing
      events$.pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.ngZone.run(() => {
          if (!this.isWarningActive) {
            this.resetTimers();
          }
        });
      });

      // Start initial timers
      this.ngZone.run(() => {
        this.resetTimers();
      });
    });
  }

  /**
   * Reset all timers (called on user activity)
   */
  resetTimers(): void {
    // Clear existing timers
    this.clearTimers();
    this.isWarningActive = false;

    // Set warning timer (triggers before actual timeout)
    this.warningTimer = setTimeout(() => {
      this.showWarning();
    }, this.IDLE_TIMEOUT - this.WARNING_TIME);
  }

  /**
   * Show warning and start countdown
   */
  private showWarning(): void {
    this.isWarningActive = true;
    this.warningStartTime = Date.now();
    
    // Emit initial warning with remaining time in seconds
    const remainingSeconds = Math.floor(this.WARNING_TIME / 1000);
    this.idleWarning$.next(remainingSeconds);

    // Start countdown interval
    this.startCountdown();

    // Set timeout for final logout
    this.timeoutTimer = setTimeout(() => {
      this.triggerTimeout();
    }, this.WARNING_TIME);
  }

  /**
   * Start countdown timer that updates every second
   */
  private startCountdown(): void {
    this.countdownInterval = setInterval(() => {
      const elapsed = Date.now() - this.warningStartTime;
      const remaining = Math.max(0, Math.floor((this.WARNING_TIME - elapsed) / 1000));
      
      if (remaining > 0) {
        this.idleWarning$.next(remaining);
      } else {
        this.clearCountdown();
        this.triggerTimeout();
      }
    }, 1000);
  }

  /**
   * Clear countdown interval
   */
  private clearCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  /**
   * Trigger timeout (logout)
   */
  private triggerTimeout(): void {
    this.clearTimers();
    this.idleTimeout$.next();
  }

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
    this.clearCountdown();
  }

  /**
   * Stop watching (cleanup)
   */
  stopWatching(): void {
    this.isWatching = false;
    this.clearTimers();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check if warning is currently active
   */
  isWarningShowing(): boolean {
    return this.isWarningActive;
  }

  /**
   * Get configuration for testing/debugging
   */
  getConfig() {
    return {
      testingMode: this.TESTING_MODE,
      idleTimeout: this.IDLE_TIMEOUT,
      warningTime: this.WARNING_TIME,
      idleTimeoutMinutes: this.IDLE_TIMEOUT / 60000,
      warningTimeSeconds: this.WARNING_TIME / 1000
    };
  }
}
